import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { WidgetConsentimientoComponent } from '../widget-consentimiento/widget-consentimiento.component';
import { environment } from '../../../environments/environment';

interface ColoresGenerico {
  texto: string;
  fondo: string;
  boton: string;
  textoBoton: string;
  /** Opcional — solo /web1consentimiento lo pisa hoy (letra de "Quiero ver más
   * información" en gris placeholder). Si no viene, cae al default fijo de
   * ConsentimientoWidgetComponent (#1b2635), igual que el resto de las páginas. */
  masInfo?: string;
}

/** Look neutro original del widget (antes de que existiera cualquier tema de marca) —
 * el default si una ruta no especifica `data.colores` (ej. /consentimiento). */
const COLORES_DEFAULT: ColoresGenerico = {
  texto: '#ffffff',
  // #3C425073 = rgba(60, 66, 80, 0.45) en hex de 8 dígitos (#RRGGBBAA).
  fondo: '#3C425073',
  boton: '#55606b',
  textoBoton: '#ffffff',
};

/**
 * Página genérica sin marca — sin nav, sin logo, solo el formulario (a diferencia de
 * /Kaufmann y /andes-motor, acá no hay ninguna página anfitriona que simular).
 *
 * La usan varias rutas (ver app.routes.ts), mismo componente, cada una con su propia
 * combinación vía `data`:
 * - /consentimiento: colores neutros (default), pegado a la izquierda.
 * - /web1consentimiento: mismos colores neutros, pegado a la izquierda.
 * - /web2consentimiento: fondo negro, letras blancas.
 * - /web3consentimiento: fondo blanco, letras negras.
 * - /webconsentimiento: redirect a /web1consentimiento (nombre viejo).
 *
 * `tema=null` explícito en el HTML (WidgetConsentimientoComponent por defecto trae
 * 'kaufmann') deja el look de cada ruta puro, sin mezclar con ningún tema de marca.
 */
@Component({
  selector: 'app-consentimiento-generico',
  standalone: true,
  imports: [WidgetConsentimientoComponent],
  templateUrl: './consentimiento-generico.component.html',
})
export class ConsentimientoGenericoComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);

  posicion: 'centro' | 'derecha' | 'izquierda' = 'centro';
  colores: ColoresGenerico = COLORES_DEFAULT;

  /**
   * 16-sep-2026, a pedido explícito: esta página es la que se usa para probar cómo se ve el
   * widget al ser invocado por cualquier página host real, con cualquier formulario — a
   * diferencia de /Kaufmann y /andes-motor, que están fijas a una sociedad/tema predeterminados.
   * Los query params son solo para poder probar eso desde la URL (?cod-sociedad=...&rut=...) —
   * en producción una página host pasaría estos mismos datos como @Input() de
   * WidgetConsentimientoComponent, no por query string; esto es únicamente conveniencia de
   * prueba local. Si un param no viene, se usa el default de WidgetConsentimientoComponent.
   */
  codSociedad = 'CONS-KAUF';
  codTipoFormulario = environment.codTipoFormulario;
  rut: string | null = null;
  nombres: string | null = null;
  apellidos: string | null = null;
  email: string | null = null;
  telefono: string | null = null;

  ngOnInit(): void {
    this.posicion =
      (this.route.snapshot.data['posicion'] as 'centro' | 'derecha' | 'izquierda' | undefined) ?? 'centro';
    this.colores = (this.route.snapshot.data['colores'] as ColoresGenerico | undefined) ?? COLORES_DEFAULT;

    const q = this.route.snapshot.queryParamMap;
    const posicionQuery = q.get('posicion') as 'centro' | 'derecha' | 'izquierda' | null;
    this.posicion = posicionQuery ?? this.posicion;
    this.codSociedad = q.get('cod-sociedad') ?? this.codSociedad;
    this.codTipoFormulario = q.get('cod-tipo-formulario') ?? this.codTipoFormulario;
    this.rut = q.get('rut');
    this.nombres = q.get('nombres');
    this.apellidos = q.get('apellidos');
    this.email = q.get('email');
    this.telefono = q.get('telefono');
  }
}
